import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { cn, slugify } from "../../../lib/utils";
import * as Switch from "@radix-ui/react-switch";
import { initialValues, validationSchemas } from "../../../lib/forms-utils";
import { TranslatedInput } from "../../ui/translated-input";
import { TranslatedTextarea } from "../../ui/translated-textarea";
import { TemplateSettingsForm as TemplateSettings } from "../../../types/forms";
import * as Checkbox from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { listCategoriesForFormsWithSubcategories } from "../../../api/admin/categories";
import { PiArrowElbowDownRightBold } from "react-icons/pi";
import { listProjectsForForms } from "../../../api/admin/projects";
import { TagsInput } from "../../ui/tags-input";
import { listPrintLocationsForForms } from "../../../api/admin/print-locations";
import PrintLocationsSelectionForm from "./print-locations-selection-form";

interface Props {
  templateSettings: TemplateSettings;
  setTemplateSettings: React.Dispatch<React.SetStateAction<TemplateSettings>>;
  isForDialog?: boolean;
  setFormHasErrors?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CheckBoxComponent = ({
  entity,
  type,
  isSelected,
  onChange,
  parentId,
}: any) => {
  const checkboxId =
    type === "subcategory" && parentId
      ? `${entity._id}_${parentId}`
      : entity._id;

  return (
    <div
      className={`flex items-center my-2 select-none ${
        type === "subcategory" ? "pl-2" : ""
      }`}
    >
      {type === "subcategory" && (
        <PiArrowElbowDownRightBold className="text-primary mr-1 pointer-events-none" />
      )}

      {/* Custom checkbox */}
      <div
        id={checkboxId}
        onClick={() => onChange(!isSelected)}
        className={`w-[25px] h-[25px] rounded bg-black flex items-center justify-center cursor-pointer 
      focus:outline-none focus:ring hover:text-primary
      will-change-transform transform translateZ-0 select-none`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onChange(!isSelected);
          }
        }}
      >
        {isSelected && <CheckIcon className="text-primary" />}
      </div>

      <label
        htmlFor={checkboxId}
        className="ml-2 select-none cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          onChange(!isSelected);
        }}
      >
        {entity.internalName ?? entity.name}
      </label>
    </div>
  );
};

const TemplateSettingsForm: React.FC<Props> = ({
  templateSettings,
  setTemplateSettings,
  isForDialog = true,
  setFormHasErrors,
}) => {
  const [categoriesWithSubcategories, setCategoriesWithSubcategories] =
    useState<any>([]);

  const [projects, setProjects] = useState<any>([]);

  // print locations
  const [printLocations, setPrintLocations] = useState<any>([]);

  // Formik schema for the form
  const formik = useFormik({
    initialValues: initialValues.templateCreateOrUpdate(templateSettings),
    validationSchema: validationSchemas.templateSettings,
    onSubmit: () => {},
  });

  // Sync Formik state with parent state
  useEffect(() => {
    setTemplateSettings((prevSettings) => ({
      ...prevSettings,
      internalName: formik.values.internalName,
      slug: formik.values.slug,
      displayName: formik.values.displayName,
      description: formik.values.description,
      fitCategory: formik.values.fitCategory,
      isPublished: formik.values.isPublished,
      projectIds: formik.values.projectIds,
      tags: formik.values.tags,
      categorySelections: formik.values.categorySelections,
      printLocations: formik.values.printLocations,
      fetchOnlyWithinSets: formik.values.fetchOnlyWithinSets,
      sharedCustomization: formik.values.sharedCustomization,
    }));
  }, [formik.values]);

  // sync error if any with parent.
  useEffect(() => {
    // call parent error setter.
    if (setFormHasErrors) {
      setFormHasErrors(Object.keys(formik.errors).length > 0);
    }
  }, [formik.errors, formik.touched, formik.values]);

  // Handle slug update on internal name blur
  const handleOnNameBlur = (e: any) => {
    const name = e.target.value;
    const slugifiedName = slugify(name);
    formik.setFieldValue("slug", slugifiedName);
  };

  const loadCategoriesForFormWithSubcategories = async () => {
    const response = await listCategoriesForFormsWithSubcategories();

    if (!response || response.status !== 200) {
      return;
    }

    setCategoriesWithSubcategories(response.data);
  };

  const loadProjectsForForms = async () => {
    const response = await listProjectsForForms();

    if (!response || response.status !== 200) {
      return;
    }

    setProjects(response.data);
  };

  const loadPrintLocationsForForms = async () => {
    const response = await listPrintLocationsForForms();

    if (!response || response.status !== 200) {
      return;
    }

    setPrintLocations(response.data);
  };

  useEffect(() => {
    loadCategoriesForFormWithSubcategories();
    loadProjectsForForms();
    loadPrintLocationsForForms();
  }, []);

  // new logic handling category and subcategory selection
  // in a map which is more efficient than the previous one.
  // categorySelections: [{categoryId: string, subcategoryIds: string[]}]
  const handleOnCategoryOrSubcategoryChange = (
    type = "category",
    isChecked: boolean,
    category: any,
    parentId: null | string = null // if subcategory, pass the parent category id,
    //  because same subcategory can belong to multiple categories, so we need the parent id under which was removed.
  ) => {
    // copy the array to avoid mutation.
    let categorySelections = [...formik.values.categorySelections];

    // if category btn is clicked, and checked.
    // add the category with all its subcategories.
    // if category btn is clicked, and unchecked.
    // remove the category with all its subcategories.

    // if subcategory btn is clicked, and checked.
    // add the subcategory under the parent category, if not already added.

    // if subcategory btn is clicked, and unchecked.
    // remove the subcategory under the parent category, but keep the parent category,
    // if there are no more subcategories under it. otherwise keep it.

    // always check for duplications before adding.

    if (type === "category") {
      if (isChecked) {
        // add the category with all its subcategories.
        const categoryAlreadyExists = categorySelections.some(
          (selection: any) => selection.categoryId === category._id
        );

        if (!categoryAlreadyExists) {
          // category does not exist, so add it with all its subcategories.
          categorySelections.push({
            categoryId: category._id,
            subcategoryIds: category.subcategories.map((sub: any) => sub._id),
          });
        } else {
          // if category already exists, then add all its subcategories.
          const categorySelection = categorySelections.find(
            (selection: any) => selection.categoryId === category._id
          );

          if (categorySelection) {
            categorySelection.subcategoryIds = category.subcategories.map(
              (sub: any) => sub._id
            );
          }
        }
      } else {
        // remove the category with all its subcategories.
        categorySelections = categorySelections.filter(
          (selection: any) => selection.categoryId !== category._id
        );
      }
    } else {
      // subcategory
      if (isChecked) {
        // we need to add the subcategory under the parent category, if not already added.
        // if parent category does not exist, then add it with the subcategory.

        const parentCategory = categorySelections.find(
          (selection: any) => selection.categoryId === parentId
        );

        if (!parentCategory) {
          // parent category does not exist, so add it with the subcategory.
          categorySelections.push({
            categoryId: parentId as string, // will come with subcategory clicks.
            subcategoryIds: [category._id],
          });
        } else {
          // parent category exists, so add the subcategory under it.
          parentCategory.subcategoryIds.push(category._id);
        }
      } else {
        // remove the subcategory under the parent category.
        // also remove the parent category if there are no more subcategories under it.
        const parentCategory = categorySelections.find(
          (selection: any) => selection.categoryId === parentId
        );

        if (parentCategory) {
          parentCategory.subcategoryIds = parentCategory.subcategoryIds.filter(
            (subId: string) => subId !== category._id
          );

          // if there are no more subcategories under the parent category, remove it.
          if (parentCategory.subcategoryIds.length === 0) {
            categorySelections = categorySelections.filter(
              (selection: any) => selection.categoryId !== parentId
            );
          }
        }
      }
    }

    formik.setFieldValue("categorySelections", categorySelections);
  };

  const handleProjectChange = (isChecked: boolean, project: any) => {
    let projectIds = formik.values.projectIds || [];

    if (isChecked) {
      projectIds.push(project._id);
    } else {
      projectIds = projectIds.filter((id: string) => id !== project._id);
    }

    formik.setFieldValue("projectIds", projectIds);
  };

  return (
    <div className="flex flex-col space-y-4 h-full w-full">
      {isForDialog && <h3 className="text-xs uppercase">Template Settings</h3>}

      <div
        className={cn(
          "w-full h-full pt-2 flex items-start gap-x-2",
          isForDialog ? "border-t border-borderColor" : ""
        )}
      >
        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">
            Internal Name *
          </Label>
          <Input
            name="internalName"
            type="text"
            id={"internalName"}
            placeholder="Internal Name"
            value={formik.values.internalName}
            onChange={formik.handleChange}
            onBlur={(e) => {
              formik.handleBlur(e);
              handleOnNameBlur(e);
            }}
            error={
              formik.touched.internalName && formik.errors.internalName
                ? formik.errors.internalName
                : ""
            }
            className="input-ring bg-transparent text-white placeholder"
          />
        </div>

        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">Slug *</Label>
          <Input
            name="slug"
            type="text"
            id={"slug"}
            placeholder="slug"
            value={formik.values.slug}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.slug && formik.errors.slug
                ? formik.errors.slug
                : ""
            }
            className="input-ring bg-transparent text-white placeholder"
          />
        </div>
      </div>

      <div className="w-full h-full flex pt-2">
        <TranslatedInput
          name="displayName"
          id="displayName"
          placeholder="Display Name"
          columns={2}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          values={formik.values.displayName}
          errors={formik.errors.displayName}
          touched={formik.touched.displayName}
        />
      </div>

      <div className="w-full h-full pt-2 flex items-end">
        <TranslatedTextarea
          name="description"
          placeholder="Description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          values={formik.values.description}
          errors={formik.errors.description}
          touched={formik.touched.description}
          columns={2}
          isEnglishMandatory={false}
        />
      </div>

      <div className="w-full h-full flex items-start gap-x-2 pt-2 mb-10">
        <div className="w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">
            Categories & Subcategories
          </Label>

          <ScrollArea.Root className="w-full relative h-[200px] overflow-hidden">
            <ScrollArea.Viewport className="w-full h-full py-2 px-2 relative rounded bg-borderColor">
              {categoriesWithSubcategories.map((category: any, idx: number) => {
                // new logic to handle category and subcategory selection.

                const categorySelected = formik.values.categorySelections?.some(
                  (selection: any) => selection.categoryId === category._id
                );

                const Subcategories = category.subcategories;

                return (
                  <React.Fragment key={category._id}>
                    <CheckBoxComponent
                      key={category._id}
                      entity={category}
                      type="category"
                      isSelected={categorySelected}
                      onChange={(checked: boolean) => {
                        handleOnCategoryOrSubcategoryChange(
                          "category",
                          checked,
                          category,
                          null
                        );
                      }}
                    />
                    {Subcategories.map((subcategory: any) => {
                      const subcategorySelected =
                        formik.values.categorySelections?.some(
                          (selection: any) =>
                            selection.categoryId === category._id &&
                            selection.subcategoryIds.includes(subcategory._id)
                        );
                      return (
                        <CheckBoxComponent
                          key={`${subcategory._id}_${category._id}`}
                          parentId={category._id}
                          entity={subcategory}
                          type="subcategory"
                          isSelected={subcategorySelected}
                          onChange={(checked: boolean) => {
                            handleOnCategoryOrSubcategoryChange(
                              "subcategory",
                              checked,
                              subcategory,
                              category._id
                            );
                          }}
                          isCategory={false}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </ScrollArea.Viewport>

            {/* Ensure scrollbar stays inside the scrollable box */}
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </div>

        <div className="w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">
            Belongs to Project(s)
          </Label>

          <ScrollArea.Root className="w-full relative h-[200px] overflow-hidden">
            <ScrollArea.Viewport className="w-full h-full py-2 px-2 relative rounded bg-borderColor">
              {projects.map((project: any, idx: number) => {
                const projectSelected = formik.values.projectIds?.includes(
                  project._id
                );

                return (
                  <React.Fragment key={project._id}>
                    <CheckBoxComponent
                      key={project._id}
                      entity={project}
                      type="project"
                      isSelected={projectSelected}
                      onChange={(checked: boolean) => {
                        handleProjectChange(checked, project);
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </ScrollArea.Viewport>

            {/* Ensure scrollbar stays inside the scrollable box */}
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </div>
      </div>

      <div className="w-full h-full pt-2 flex items-start gap-x-2">
        <div className="w-1/2 flex flex-col items-start">
          <PrintLocationsSelectionForm
            printLocationsList={printLocations}
            selectedPrintLocations={formik.values.printLocations}
            setSelectedPrintLocations={(printLocations: any) => {
              formik.setFieldValue("printLocations", printLocations);
            }}
          />
        </div>

        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">Tags</Label>
          <TagsInput
            tags={formik.values.tags}
            onTagsChange={(tags) => {
              formik.setFieldValue("tags", tags);
            }}
          />
        </div>
      </div>

      <div className="w-full  h-full pt-2 flex items-start gap-x-2">
        <div className="my-2 w-1/2 flex items-start">
          <div className=" w-1/2 flex flex-col items-start">
            <Label className="block text-body mb-2 text-sm px-1">
              Fit Category
            </Label>

            <div className="ps-1 flex items-center justify-center">
              <Checkbox.Root
                className="bg-black w-[25px] h-[25px] rounded hover:text-primary focus:ring outline-none"
                checked={formik.values.fitCategory?.light}
                onCheckedChange={(newValue) => {
                  formik.setFieldValue("fitCategory.light", newValue);
                }}
              >
                <Checkbox.Indicator className="text-primary">
                  <CheckIcon />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <label className="pl-2 font-semibold text-sm leading-none text-white">
                Light{" "}
              </label>
            </div>

            <div className="ps-1 mt-2 flex items-center justify-center">
              <Checkbox.Root
                className="bg-black w-[25px] h-[25px] rounded hover:text-primary focus:ring outline-none"
                checked={formik.values.fitCategory?.dark}
                onCheckedChange={(newValue) => {
                  formik.setFieldValue("fitCategory.dark", newValue);
                }}
              >
                <Checkbox.Indicator className="text-primary">
                  <CheckIcon />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <label className="pl-2 font-semibold text-sm leading-none text-white">
                Dark{" "}
              </label>
            </div>
          </div>

          <div>
            <Label className="block text-body mb-2 text-sm px-1">
              Shared Customization
            </Label>

            <Switch.Root
              className="bg-borderColor hover:cursor-pointer ms-1 w-[39px] h-[20px] rounded-full relative focus:shadow-black data-[state=checked]:bg-primary outline-none cursor-default"
              checked={formik.values.sharedCustomization}
              onCheckedChange={(newValue) => {
                formik.setFieldValue("sharedCustomization", newValue);
              }}
            >
              <Switch.Thumb className="data-[disabled]:opacity-50 block w-[20px] h-[21px] bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
            </Switch.Root>
          </div>
        </div>

        <div className="my-2 w-1/2 flex items-start gap-x-12">
          <div>
            <Label className="block text-body mb-2 text-sm px-1">
              Published
            </Label>
            <Switch.Root
              className="bg-borderColor hover:cursor-pointer ms-1 w-[39px] h-[20px] rounded-full relative focus:shadow-black data-[state=checked]:bg-primary outline-none cursor-default"
              checked={formik.values.isPublished}
              onCheckedChange={(newValue) => {
                if (newValue === false) {
                  formik.setFieldValue("isPublished", false);
                  // also reset the fetchOnlyWithinSets value.
                  formik.setFieldValue("fetchOnlyWithinSets", false);
                } else {
                  formik.setFieldValue("isPublished", newValue);
                }
              }}
            >
              <Switch.Thumb className="block w-[20px] h-[21px] bg-white rounded-full  transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
            </Switch.Root>
          </div>

          <div>
            <Label className="block text-body mb-2 text-sm px-1">
              Fetch Only Within Sets
            </Label>
            <Switch.Root
              className="bg-borderColor hover:cursor-pointer ms-1 w-[39px] h-[20px] rounded-full relative focus:shadow-black data-[state=checked]:bg-red-600 outline-none cursor-default"
              checked={formik.values.fetchOnlyWithinSets}
              onCheckedChange={(newValue) => {
                formik.setFieldValue("fetchOnlyWithinSets", newValue);
              }}
              disabled={!formik.values.isPublished}
            >
              <Switch.Thumb className="data-[disabled]:opacity-50 block w-[20px] h-[21px] bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
            </Switch.Root>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSettingsForm;
